import { IIntegrationApiKey } from "../../../@types/admin/AdminIntegrationApiKey";
import { useUpdateIntegrationApiKey } from "../../../hooks/mutations/admin/integrationApiKeys";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { KeyPrefix } from "./AdminIntegrationApiKeys.styles";

interface IRevokeIntegrationApiKeyModalProps {
  apiKey: IIntegrationApiKey;
  onClose: () => void;
}

/**
 * Revoke confirmation. Revoking is a PUT with `active: false` — the key row
 * stays, so it can be reactivated later, but every consumer holding the
 * value is locked out the moment this saves.
 */
export function RevokeIntegrationApiKeyModal({
  apiKey,
  onClose,
}: IRevokeIntegrationApiKeyModalProps) {
  const { showToast } = useToast();
  const { updateApiKey, isUpdatingApiKey } = useUpdateIntegrationApiKey();

  const handleRevoke = async () => {
    try {
      await updateApiKey({
        id: apiKey.id,
        request: {
          description: apiKey.description,
          claims: apiKey.claims,
          expiresIn: apiKey.expiresIn,
          active: false,
        },
      });
      showToast("The API key was revoked.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error revoking this API key."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer data-testid="api-key-revoke-modal">
      <ModalTitle>Revoke API key</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Revoke <strong>{apiKey.description}</strong>
          {apiKey.keyPrefix ? (
            <>
              {" "}
              (<KeyPrefix>{apiKey.keyPrefix}…</KeyPrefix>)
            </>
          ) : null}
          ?
        </ModalBodyText>
        <WarningNotice>
          Every integration using this key stops authenticating immediately. The
          key can be reactivated from this page if needed.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isUpdatingApiKey}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isUpdatingApiKey}
            onClick={handleRevoke}
            data-testid="api-key-revoke-confirm"
          >
            {isUpdatingApiKey ? "Revoking..." : "Revoke"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
